import React, { useEffect } from 'react';
import { Portal, Modal } from 'react-native-paper';
import styled from '@emotion/native';
import { Linking, ScrollView } from 'react-native';

import useStore from '@/stores/useStore';
import useRouteThemeStore from '@/stores/useRouteThemeStore';

import Button from '@/shared/components/Button';
import { BodyText, H2 } from '@/shared/components/StyledText';
import { green, sand } from '@/shared/styles/colors';
import { EMME_APP_STORE_URL } from '@/shared/constants/utility';
import SentryService from '@/services/sentry/Sentry.service';

const AppVersionModal = () => {
  const { setOverride, clearOverride } = useRouteThemeStore();

  const { isAppVersionModalVisible } = useStore();

  // Override the safe area theming on iOS
  useEffect(() => {
    if (isAppVersionModalVisible) {
      setOverride({
        topSafeAreaBackground: green,
        bottomSafeAreaBackground: green,
        statusBarStyle: 'light-content',
      });
    }

    if (!isAppVersionModalVisible) {
      clearOverride();
    }
  }, [clearOverride, isAppVersionModalVisible, setOverride]);

  const handleOpenAppStore = async () => {
    try {
      await Linking.openURL(EMME_APP_STORE_URL);
    } catch (error) {
      SentryService.captureError('Error opening App Store', error);
    }
  };

  return (
    <Portal>
      <Modal
        visible={isAppVersionModalVisible}
        dismissable={false}
        style={{ alignItems: 'center' }}
        contentContainerStyle={{ flex: 1, width: '100%' }}
        theme={{
          colors: {
            backdrop: 'transparent',
          },
        }}
      >
        <ScrollView
          style={{ width: '100%', height: '100%' }}
          contentContainerStyle={{
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <Container>
            <MainContentContainer>
              <TitleContainer>
                <Title>New Version Available</Title>
              </TitleContainer>
              <CopyContainer>
                <StyledText marginBottom={8}>
                  Update now to access the latest features and improvements.
                </StyledText>
                <StyledText marginBottom={30}>Required to continue.</StyledText>
              </CopyContainer>
              <ButtonContainer>
                <Button
                  text="UPDATE APP"
                  variant="secondary"
                  size="medium"
                  onPress={handleOpenAppStore}
                />
              </ButtonContainer>
            </MainContentContainer>
          </Container>
        </ScrollView>
      </Modal>
    </Portal>
  );
};

export default AppVersionModal;

const Container = styled.View({
  paddingTop: 68,
  paddingBottom: 48,
  alignItems: 'center',
  width: '100%',
  backgroundColor: green,
  borderRadius: 0,
  maxWidth: '100%',
  height: '100%',
  paddingHorizontal: 30,
});

const MainContentContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
});

const TitleContainer = styled.View({
  marginBottom: 24,
});

const Title = styled(H2)({
  color: sand,
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: 0,
  lineHeight: 35.9,
});

const CopyContainer = styled.View({
  width: '100%',
  maxWidth: 322,
  alignItems: 'center',
});

const ButtonContainer = styled.View({
  width: '100%',
  maxWidth: 330,
  marginHorizontal: 'auto',
});

const StyledText = styled(BodyText)<{ marginBottom?: number }>(
  ({ marginBottom }) => ({
    width: '85%',
    color: sand,
    textAlign: 'center',
    fontSize: 16,
    lineHeight: 21,
    ...(marginBottom && { marginBottom }),
  }),
);
